import {
  AlertTriangle,
  CalendarOff,
  Check,
  CirclePause,
  CirclePlay,
  CheckCircle2,
  Settings,
  X,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { UserAvatar } from "@/components/user-avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useDecideMembership,
  useJoinRequests,
  useSetDayStatus,
  useUpdateGroup,
} from "@/hooks/use-groups";
import { t } from "@/i18n/pl";
import { formatDays, todayWeekday } from "@/lib/format";
import type { GroupDetail } from "@/types/api";

/** Two-tap day status + join-request inbox — the whole driver's cockpit. */
export function DriverPanel({ group }: { group: GroupDetail }) {
  const { data: requests } = useJoinRequests(group.id, true);
  const decide = useDecideMembership();
  const setStatus = useSetDayStatus(group.id);
  const updateGroup = useUpdateGroup(group.id);

  const [delayOpen, setDelayOpen] = useState(false);
  const [delayMinutes, setDelayMinutes] = useState("10");
  const [note, setNote] = useState("");

  // Mirrors the API's 1–180 range so the button, not a 422 toast, says no —
  // and an emptied field no longer makes "Zapisz" silently do nothing.
  const minutes = Number(delayMinutes);
  const delayValid = delayMinutes.trim() !== "" && Number.isInteger(minutes) && minutes >= 1 && minutes <= 180;

  // A status only exists for a day the car runs (the API refuses the rest):
  // a driver with a morning and an afternoon crew would otherwise cancel the
  // wrong one from the card of the group that stays home today.
  const today = todayWeekday();
  const drivesToday = group.days.includes(today);
  const nextDay = [1, 2, 3, 4, 5, 6]
    .map((offset) => (today + offset) % 7)
    .find((day) => group.days.includes(day));

  function submitDelay() {
    if (!delayValid) return;
    setStatus.mutate(
      { status: "delayed", delay_minutes: minutes, note: note.trim() || undefined },
      { onSuccess: () => setDelayOpen(false) },
    );
  }

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h3 className="mb-2 text-sm font-semibold tracking-wide uppercase opacity-70">
          {group.status === "paused" ? t.driver.pausedTitle : t.driver.todayControls}
        </h3>
        {/* A paused car (its driver was deactivated, then reactivated) is
            invisible to everyone else; without this the driver would see an
            ordinary card and never learn why nobody asks to join. */}
        {group.status === "paused" ? (
          <div className="flex flex-col gap-3">
            <p className="flex items-center gap-2 text-sm opacity-80">
              <CirclePause className="size-4 shrink-0" aria-hidden />
              {t.driver.pausedBody}
            </p>
            <Button
              variant="outline"
              className="text-foreground self-start"
              disabled={updateGroup.isPending}
              onClick={() =>
                updateGroup.mutate(
                  { status: "active" },
                  { onSuccess: () => toast.success(t.driver.resumed) },
                )
              }
            >
              <CirclePlay aria-hidden /> {t.driver.resume}
            </Button>
          </div>
        ) : drivesToday ? (
          <div className="grid grid-cols-3 gap-2">
            {/* text-foreground is load-bearing on all three: the outline variant
                sets no text color of its own, and these sit inside the navy card
                where the inherited color is cream — cream on a light button is
                unreadable until hover. */}
            <Button
              variant="outline"
              className="text-foreground h-12 flex-col gap-0.5 border-[oklch(0.7_0.1_124)] text-xs font-semibold"
              disabled={setStatus.isPending}
              onClick={() => setStatus.mutate({ status: "on_time" })}
            >
              <CheckCircle2 className="size-4 text-[oklch(0.55_0.12_124)]" aria-hidden />
              {t.status.setOnTime}
            </Button>
            <Button
              variant="outline"
              className="text-foreground h-12 flex-col gap-0.5 border-[oklch(0.8_0.08_80)] text-xs font-semibold"
              disabled={setStatus.isPending}
              onClick={() => {
                // Yesterday's minutes and note must not be pre-filled today.
                setDelayMinutes("10");
                setNote("");
                setDelayOpen(true);
              }}
            >
              <AlertTriangle className="size-4 text-[oklch(0.6_0.1_70)]" aria-hidden />
              {t.status.setDelayed}
            </Button>
            <Button
              variant="outline"
              className="border-destructive/40 text-foreground h-12 flex-col gap-0.5 text-xs font-semibold"
              disabled={setStatus.isPending}
              onClick={() => setStatus.mutate({ status: "cancelled" })}
            >
              <XCircle className="text-destructive size-4" aria-hidden />
              {t.status.setCancelled}
            </Button>
          </div>
        ) : (
          <p className="flex items-center gap-2 text-sm opacity-80">
            <CalendarOff className="size-4 shrink-0" aria-hidden />
            {t.driver.notToday(nextDay === undefined ? undefined : t.days.onDay[nextDay])}
          </p>
        )}
      </div>

      {/* text-foreground for the same reason as the status buttons above. */}
      <Button asChild variant="outline" size="sm" className="text-foreground self-start">
        <Link to={`/grupy/${group.id}/edytuj`}>
          <Settings aria-hidden /> {t.group.settings}
        </Link>
      </Button>

      {requests && requests.length > 0 && (
        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
          <h3 className="mb-2 text-sm font-semibold tracking-wide uppercase opacity-70">
            {t.driver.requests} ({requests.length})
          </h3>
          <ul className="flex flex-col gap-2">
            {requests.map((request) => (
              <li
                key={request.id}
                className="bg-card text-card-foreground flex flex-wrap items-center gap-3 rounded-xl border p-3"
              >
                <UserAvatar user={request.user} className="size-10" fallbackClassName="bg-secondary font-semibold" />
                <div className="min-w-0 flex-1 basis-32">
                  <p className="truncate text-sm font-semibold">{request.user.full_name}</p>
                  <p className="text-primary text-xs font-semibold">
                    {formatDays(request.days)}
                  </p>
                  {request.request_message && (
                    <p className="text-muted-foreground truncate text-xs">
                      „{request.request_message}"
                    </p>
                  )}
                </div>
                <Button
                  size="sm"
                  className="h-9"
                  disabled={decide.isPending}
                  onClick={() =>
                    decide.mutate({ membershipId: request.id, action: "active" })
                  }
                >
                  <Check aria-hidden /> {t.driver.accept}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-foreground h-9"
                  aria-label={t.driver.decline}
                  disabled={decide.isPending}
                  onClick={() =>
                    decide.mutate({ membershipId: request.id, action: "declined" })
                  }
                >
                  <X aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={delayOpen} onOpenChange={setDelayOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.status.delayDialogTitle}</DialogTitle>
            <DialogDescription>{t.status.notePlaceholder}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="delay-minutes">{t.status.delayMinutes}</Label>
              <Input
                id="delay-minutes"
                type="number"
                min={1}
                max={180}
                inputMode="numeric"
                value={delayMinutes}
                onChange={(event) => setDelayMinutes(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="delay-note">{t.status.noteLabel}</Label>
              <Input
                id="delay-note"
                maxLength={200}
                placeholder={t.status.notePlaceholder}
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelayOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button onClick={submitDelay} disabled={setStatus.isPending || !delayValid}>
              {t.common.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
